import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from './useAuth'

export type UserLicense = {
  id: string
  user_id: string
  license_tier: string
  total_devices: number
  annual_quota_km2: number
  purchased_at: string
  expires_at: string | null
  status: string
}

export type DeviceLicense = {
  id: string
  user_license_id: string
  user_id: string
  license_id: string
  hardware_fingerprint: string
  hardware_serial: string | null
  activated_at: string
  status: string
}

export function useLicense() {
  const { user } = useAuth()

  const {
    data: license,
    isLoading: licenseLoading,
    error: licenseError,
  } = useQuery({
    queryKey: ['user-license', user?.id],
    queryFn: async () => {
      if (!user) return null

      const { data, error } = await supabase
        .from('user_licenses')
        .select('*')
        .eq('user_id', user.id)
        .in('status', ['active', 'expired'])
        .order('purchased_at', { ascending: false })
        .limit(1)
        .single()

      if (error && error.code !== 'PGRST116') {
        // PGRST116 = no rows returned
        throw error
      }

      return data as UserLicense | null
    },
    enabled: !!user,
  })

  const {
    data: devices,
    isLoading: devicesLoading,
    error: devicesError,
  } = useQuery({
    queryKey: ['device-licenses', user?.id],
    queryFn: async () => {
      if (!user) return []

      const { data, error } = await supabase
        .from('device_licenses')
        .select('*')
        .eq('user_id', user.id)
        .in('status', ['active', 'expired'])
        .order('activated_at', { ascending: false })

      if (error) throw error

      return (data || []) as DeviceLicense[]
    },
    enabled: !!user,
  })

  const activatedDevices = new Set(devices?.map((device) => device.hardware_fingerprint)).size
  const totalDevices = license?.total_devices || 0

  return {
    license,
    devices,
    activatedDevices,
    totalDevices,
    loading: licenseLoading || devicesLoading,
    error: licenseError || devicesError,
  }
}
